import { useApolloClient } from "@apollo/client";
import { ActionIcon, Text, useMantineTheme } from "@mantine/core";
import {
  GetEmployeeStationPageDocument,
  GetEmployeeStationPageQuery,
  GetEmployeeStationPageQueryVariables,
  useDeleteEmployeeStationMutation,
} from "src/generated/graphql";
import { FetchParams, QueryResponse } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "src/api/AppContext";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_ORDER,
  EMPLOYEE_ROLE_OBJ,
  ROUTES,
  ROUTE_NAMES,
} from "src/utils/constants";
import { Button } from "@mantine/core";
import { useModals } from "@mantine/modals";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import CreateStation from "./CreateStation";
import { MdDelete } from "react-icons/md";
import useCustomModal from "src/hooks/useCustomModal";
import { useDisclosure } from "@mantine/hooks";
import useWritePermission from "src/hooks/usePermission";

export type StationData = ArrayElement<
  GetEmployeeStationPageQuery["employeeStationPage"]["items"]
>;

type Props = {
  isOrg?: boolean;
};

const PayRates = ({ isOrg = false }: Props) => {
  const modals = useModals();
  const navigate = useNavigate();
  const { setCurrentPageTitle, currentLocation } = useContext(AppContext);
  const { query } = useApolloClient();
  const { DeleteRowIcon, refetchTableData, setrefetchTableData } =
    useCustomModal();
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.employeeStations);

  const [deleteEmployeeStationEntry] = useDeleteEmployeeStationMutation({
    onCompleted: () => {
      setrefetchTableData(!refetchTableData);
    },
  });

  useEffect(() => {
    if (!currentLocation?.stationsEnabled) {
      navigate(ROUTES.DASHBOARD);
    }
  }, [currentLocation]);

  const getStations = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<StationData>> => {
    const filters = fetchParams.filters || [];
    filters?.push({
      field: "location",
      type: "text",
      value: currentLocation?.id,
    });
    let { data } = await query<
      GetEmployeeStationPageQuery,
      GetEmployeeStationPageQueryVariables
    >({
      query: GetEmployeeStationPageDocument,
      variables: {
        pagination: {
          count: fetchParams.count,
          page: fetchParams.page,
          orderBy: fetchParams.orderBy || DEFAULT_ORDER.name,
          filters: filters,
        },
      },
    });

    if (data.employeeStationPage) {
      return {
        rowCount: data.employeeStationPage.totalRows,
        rowData: data.employeeStationPage.items as StationData[],
      };
    }
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.STATIONS);
    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const onDelete = (id: string | undefined) => {
    id && deleteEmployeeStationEntry({ variables: { employeeStationId: id } });
  };

  const handleModal = useCallback(
    (isEdit: boolean = false, id?: string) => {
      modals.openModal({
        closeOnClickOutside: false,
        title: isEdit ? "Edit Station" : "Create Station",
        children: (
          <CreateStation
            isEdit={isEdit}
            employeeStationId={id || ""}
            onRefetch={() => {
              setrefetchTableData(!refetchTableData);
            }}
            isAllowWrite={isAllowWrite}
          />
        ),
      });
    },
    [isOrg, isAllowWrite, refetchTableData]
  );

  const StationsTable = useCallback(() => {
    return (
      <ScrollableDataGrid<StationData>
        topMargin={56}
        columnConfigs={[
          {
            field: "name",
            name: "Name",
            filter: {
              field: "name",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                onClick={() => handleModal(true, rowItem.id)}
                style={{ cursor: "pointer" }}
              >
                {rowItem.name}
              </Text>
            ),
          },
          {
            field: "payRate",
            name: "Pay Rate",
            filter: {
              field: "payRate",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                style={{
                  width: "40vw",
                  cursor: "pointer",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                onClick={() => {
                  // handleNavigation(rowItem.id)
                }}
              >
                $ {rowItem.payRate}
              </Text>
            ),
          },
          {
            field: "",
            name: "",
            cellRenderer: ({ id }) =>
              isAllowWrite && <DeleteRowIcon id={id} onDelete={onDelete} />,
          },
        ]}
        dataFetch={getStations}
      />
    );
  }, [refetchTableData, currentLocation, isAllowWrite]);

  return (
    <div>
      {isAllowWrite && (
        <Button style={{ marginBottom: 20 }} onClick={() => handleModal(false)}>
          Create Station +
        </Button>
      )}
      <StationsTable />
    </div>
  );
};

export default PayRates;
